import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import Donate from "@/components/Donate";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LINK_RE = /\[([^\]]+)\]\((https?:\/\/[^)]+)\)/g;

// The 7 rungs of Jacob's Ladder, with a single-line "what this rung is
// about" descriptor for the diagram tooltip.
const LADDER_RUNGS = [
  { name: "Exposure", hint: "The hidden wound comes into the light" },
  { name: "Truth", hint: "Truth replaces the lie" },
  { name: "Severance", hint: "Cutting off false refuges; forgiving the old" },
  { name: "Surrender", hint: "Letting go of all remaining trauma and lies" },
  { name: "Alignment", hint: "Learning to walk fully in the Spirit of love" },
  { name: "Union", hint: "The apostolic place — becoming love itself" },
  { name: "Glory", display: "Glory (Perpetua's Rung)", hint: "Multiplied fruit — a light on a hill" },
];

// Derive the active blockage rung from the "What Resists You Here"
// section body (which merged in the old "Active Ladder Blockage"
// diagnosis — it now opens with "You are standing on {rung}" or
// "The strongest blockage appears to be between {X} and {Y}"). In
// the between-form, the EARLIER/LOWER of the two is the rung the
// recipient is currently standing on (she has done some of that
// work but has not yet crossed into the next one). So we return
// exactly ONE rung — the lowest-numbered match — or [] if none.
// Legacy packets rendered before the merge still carry an "Active
// Ladder Blockage" section; we fall back to that for them.
function detectActiveBlockageRungs(sections) {
  if (!Array.isArray(sections)) return [];
  const firstRungIn = (body) => {
    const head = (body || "").slice(0, 400);
    for (const { name } of LADDER_RUNGS) {
      const re = new RegExp(`\\b${name}\\b`, "i");
      if (re.test(head)) return [name];
    }
    return [];
  };
  const resists = sections.find((s) => (s.title || "").toLowerCase().includes("what resists you here"));
  if (resists) {
    const rungs = firstRungIn(resists.body);
    if (rungs.length) return rungs;
  }
  const legacy = sections.find((s) => (s.title || "").toLowerCase().includes("active ladder blockage"));
  if (legacy) {
    const rungs = firstRungIn(legacy.body);
    if (rungs.length) return rungs;
  }
  return [];
}

// Slug the packet's rendered rung heading so we can jump to it via
// querySelector. Matches the h3 produced in the Jacob's Ladder section
// for lines like "6. Union", "7. Glory".
function rungHeadingId(rungName) {
  return `rung-${rungName.toLowerCase()}`;
}

// Detectors — mirror the PDF builder's routing so the on-screen packet
// renders with the same structural hierarchy as the downloaded PDF.
const RUNG_LABEL_RE =
  /^\s*[1-7][.)]\s*(Exposure|Truth|Severance|Surrender|Alignment|Union|Glory)\s*$/;
const INLINE_LABEL_RE =
  /^(Prayer for this step|Scripture to meditate on|Forgiveness|Activation|Declaration)\s*[:.]?\s*$/;
// Mirror the PDF subsection-heading regex so the on-screen packet styles
// classification subheadings (Principalities / Thrones, Dominions,
// Authorities) inside Satanic Strategy — and Foundational lies, Pressures,
// Gifts of Spiritual Fruits/Office, etc. — as bold visually-distinct labels
// rather than running them inline as plain body paragraphs.
const SUBSECTION_HEADING_RE =
  /^(?:\*\*)?(?:[A-Z][\w\s/\-–&]{0,60}\s+lies|False refuges[^.!?\n]{0,60}|How to move to\s+[A-Z][a-z]+|Pressures|Blockage|The\s+(?:Blockage|Old\s+Survival\s+System)|Gifts of Spiritual\s+(?:Fruits|Office)|Principalit(?:y|ies)(?:\s*\/\s*Thrones?)?|Dominions?|Authorities|Strongman\s*:\s*spirit\s+of[\w\s/\-]+|Supporting\s+spirits\s+flowing\s+behind\s+it|Physical\s+manifestation\s+spirits)\s*[:.]?\s*(?:\*\*)?\s*$/i;

// Inline bold patterns — mirror the PDF _to_paragraph_html rules so bold
// trauma lists, became-chains, strategy-name openers, Hidden-Gifts leads,
// and scripture labels all match across PDF and web.
function applyInlineBold(text) {
  // Markdown **bold**
  let out = text.replace(/\*\*([^*]+)\*\*/g, "<b>$1</b>");
  // Markdown *italic* (single-star) — run AFTER **bold** so we don't
  // accidentally grab `**`. Used by the diagnostic specifier line.
  out = out.replace(
    /(^|[^*])\*([^*\n]+?)\*(?!\*)/g,
    (_m, pre, inner) => `${pre}<i>${inner}</i>`,
  );
  // "Name - rest of sentence": bold the name up to " - "
  out = out.replace(
    /^([A-Z][^.!?:\n]{0,60}?)\s+[-–—]\s+(.+)$/s,
    (m, name, rest) =>
      name.trim().split(/\s+/).length <= 8 ? `<b>${name}</b> - ${rest}` : m,
  );
  // Strategy preamble: "<Name> emphasizes ..."
  out = out.replace(
    /\b(Satan|Devil|Serpent|Dragon|Accuser)\s+emphasizes\b/g,
    "<b>$1</b> emphasizes",
  );
  // Hidden Gifts lead clause: "The attack on/through X may point to Y."
  out = out.replace(
    /^(The attack (?:on|through)[^.]+\.)/,
    "<b>$1</b>",
  );
  // Lead trauma list: "the deepest breach appears to be ... ."
  out = out.replace(
    /(the deepest breach appears to be\s+)(.+?\.)/i,
    (m, a, b) => `${a}<b>${b.slice(0, -1)}</b>.`,
  );
  // "X became Y, X became Y, ..." with 3+ "became" tokens
  out = out.replace(
    /(?:[^.]*?\bbecame\b)(?:[^.]*?\bbecame\b){2,}[^.]*\./g,
    (s) => `<b>${s.slice(0, -1)}</b>.`,
  );
  return out;
}

// Safe HTML renderer — we only inject <b>/<i> tags produced by applyInlineBold
// (everything else is escaped first by React via the string split).
function renderWithBold(text) {
  const safe = applyInlineBold(text);
  // Split on <b>...</b> or <i>...</i> and render each chunk; React auto-escapes plain text.
  const segs = safe.split(/(<b>.*?<\/b>|<i>.*?<\/i>)/g);
  return segs.map((s, idx) => {
    const mb = s.match(/^<b>(.*?)<\/b>$/);
    if (mb) return <strong key={idx}>{mb[1]}</strong>;
    const mi = s.match(/^<i>(.*?)<\/i>$/);
    if (mi) return <em key={idx}>{mi[1]}</em>;
    return <span key={idx}>{s}</span>;
  });
}

function renderLines(s, baseKey) {
  const lines = s.split("\n");
  const out = [];
  lines.forEach((ln, i) => {
    out.push(<span key={`s-${baseKey}-${i}`}>{renderWithBold(ln)}</span>);
    if (i < lines.length - 1) out.push(<br key={`br-${baseKey}-${i}`} />);
  });
  return out;
}

function renderInline(text) {
  const parts = [];
  let lastIndex = 0;
  let match;
  let key = 0;
  LINK_RE.lastIndex = 0;
  while ((match = LINK_RE.exec(text)) !== null) {
    if (match.index > lastIndex) parts.push(...renderLines(text.slice(lastIndex, match.index), key++));
    parts.push(
      <a
        key={`l-${key++}`}
        href={match[2]}
        target="_blank"
        rel="noopener noreferrer"
        className="rm-link"
        data-testid="packet-worship-link"
      >
        {match[1]}
      </a>
    );
    lastIndex = match.index + match[0].length;
  }
  if (lastIndex < text.length) parts.push(...renderLines(text.slice(lastIndex), key++));
  return parts;
}

function LadderDiagram({ activeRungs }) {
  const activeSet = new Set((activeRungs || []).map((r) => r.toLowerCase()));
  const onClick = (rungName) => (e) => {
    e.preventDefault();
    const el = document.getElementById(rungHeadingId(rungName));
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };
  return (
    <aside
      className="mb-20 border border-[#FFE7A0]/30 bg-[#FFE7A0]/[0.04] px-6 sm:px-10 py-8"
      data-testid="ladder-diagram"
      aria-label="Jacob's Ladder — seven rungs"
    >
      <div className="font-cinzel text-[0.65rem] sm:text-xs tracking-[0.28em] uppercase text-[#FFE7A0] mb-5 text-center">
        Jacob's Ladder — your seven rungs
      </div>
      <ol className="relative pl-10 sm:pl-14" data-testid="ladder-rungs">
        <span
          aria-hidden="true"
          className="absolute left-4 sm:left-6 top-2 bottom-2 w-px bg-gradient-to-b from-[#FFE7A0]/60 via-[#FFE7A0]/25 to-[#FFE7A0]/60"
        />
        {LADDER_RUNGS.slice().reverse().map((rung, revIdx) => {
          const idx = LADDER_RUNGS.length - 1 - revIdx;
          const isActive = activeSet.has(rung.name.toLowerCase());
          return (
            <li key={rung.name} className="relative mb-4 last:mb-0" data-testid={`ladder-rung-${rung.name.toLowerCase()}`}>
              <span
                aria-hidden="true"
                className={`absolute -left-7 sm:-left-9 top-[0.4rem] h-3 w-3 rounded-full border ${
                  isActive
                    ? "bg-[#FFE7A0] border-[#FFE7A0] shadow-[0_0_12px_rgba(255, 231, 160,0.65)]"
                    : "bg-[#0a0f24] border-[#FFE7A0]/60"
                }`}
              />
              <button
                type="button"
                onClick={onClick(rung.name)}
                className={`group w-full text-left transition-colors duration-300 ${
                  isActive
                    ? "text-[#FFE7A0]"
                    : "text-[#F0E6D2]/85 hover:text-[#FFE7A0]"
                }`}
                data-testid={`ladder-rung-link-${rung.name.toLowerCase()}`}
              >
                <span className="font-serif-display text-base sm:text-lg leading-tight">
                  {idx + 1}. {rung.display || rung.name}
                  {isActive && (
                    <span className="ml-3 font-cinzel text-[0.55rem] tracking-[0.25em] uppercase text-[#FFE7A0]/85">
                      where you are right now
                    </span>
                  )}
                </span>
                <span className="block text-xs sm:text-sm text-[#F0E6D2]/55 italic font-body font-light mt-0.5 group-hover:text-[#F0E6D2]/80">
                  {rung.hint}
                </span>
              </button>
            </li>
          );
        })}
      </ol>
      {activeRungs && activeRungs.length > 0 && (
        <p className="mt-6 text-center text-xs sm:text-sm text-[#F0E6D2]/60 font-body font-light italic" data-testid="ladder-diagram-note">
          The gold rung marks where the Matrix sees you standing today. Tap any rung to jump to it.
        </p>
      )}
    </aside>
  );
}

export default function Packet() {
  const { id } = useParams();
  const [packet, setPacket] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      // Retry-with-backoff for the eventual-consistency window: the
      // cloud-job poll on the previous page just saw status=ready and
      // navigated here. Worker A wrote the packet to disk, but if the
      // very next GET lands on worker B before the shared volume
      // syncs the new file, the first call can 404. We back off
      // through several short attempts before showing "not found" —
      // this absorbs cross-worker latency on production.
      const delays = [0, 800, 1600, 2500, 4000]; // ~9 seconds total
      let lastErrStatus = 0;
      for (let i = 0; i < delays.length; i += 1) {
        if (cancelled) return;
        if (delays[i] > 0) await new Promise((r) => setTimeout(r, delays[i]));
        try {
          const { data } = await axios.get(`${API}/packets/${id}`);
          if (!cancelled) setPacket(data);
          return;
        } catch (e) {
          lastErrStatus = e?.response?.status || 0;
          // Only retry on 404 (eventual consistency). Any other error
          // is a real failure — surface it immediately.
          if (lastErrStatus && lastErrStatus !== 404) {
            if (!cancelled) setErr("Packet not found.");
            return;
          }
        }
      }
      if (!cancelled) setErr("Packet not found.");
    })();
    return () => {
      cancelled = true;
    };
  }, [id]);

  const download = async () => {
    const target = `${API}/packets/${id}/pdf`;
    const safeName = (packet?.name || "packet").replace(/[^a-z0-9_\- ]/gi, "").trim().replace(/\s+/g, "_") || "packet";
    const filename = `RestorationMatrix_${safeName}.pdf`;

    // Detect mobile/iOS where blob downloads are unreliable. On those
    // browsers, just navigate to the URL — the backend already sets
    // Content-Disposition: attachment so the browser will save (or open
    // its native PDF viewer where the user can save from there).
    const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent || "");
    if (isMobile) {
      window.location.href = target;
      return;
    }

    // Desktop blob download — fast, no extra tab.
    try {
      const res = await fetch(target);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 4000);
    } catch {
      // Last-resort fallback: navigate directly so the browser's native
      // download (or PDF viewer) handles it.
      window.location.href = target;
    }
  };

  if (err) {
    return (
      <main className="relative min-h-screen flex items-center justify-center" data-testid="packet-error-page">
        <div className="text-center">
          <p className="font-serif-display text-3xl text-[#F0E6D2] mb-6">{err}</p>
          <Link to="/intake" className="rm-btn" data-testid="packet-error-retry">Begin Again</Link>
        </div>
      </main>
    );
  }

  if (!packet) {
    return (
      <main className="relative min-h-screen flex items-center justify-center" data-testid="packet-loading">
        <p className="overline rm-shimmer">Unsealing…</p>
      </main>
    );
  }

  const createdPretty = (() => {
    try {
      return new Date(packet.created_at).toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" });
    } catch { return packet.created_at; }
  })();

  return (
    <main className="relative min-h-screen" data-testid="packet-page">
      {/* FULL-BLEED atmospheric DNA + geometry behind everything */}
      <div className="fixed inset-0 z-0 pointer-events-none" aria-hidden="true">
        <div className="absolute inset-0 bg-center bg-no-repeat bg-cover opacity-25"
             style={{
               backgroundImage: "url('/visuals/art_dna_geometry.png')",
               backgroundSize: "min(110vh, 110vw)",
             }} />
        <div className="absolute inset-0"
             style={{
               background:
                 "radial-gradient(ellipse at top, rgba(5,8,24,0.55) 0%, rgba(5,8,24,0.92) 60%, rgba(5,8,24,0.98) 100%)",
             }} />
      </div>

      <header className="sticky top-0 z-30 bg-[#050818]/80 backdrop-blur-xl border-b border-[#FFE7A0]/10">
        <div className="max-w-4xl mx-auto px-6 sm:px-12 md:px-16 py-5 flex items-center justify-between">
          <Link to="/" className="font-cinzel text-[0.7rem] text-[#FFE7A0]" data-testid="packet-brand">
            THE RESTORATION MATRIX
          </Link>
          <div className="flex items-center gap-4">
            <button
              type="button"
              onClick={() => document.querySelector('[data-testid="donate-card"]')?.scrollIntoView({ behavior: "smooth", block: "center" })}
              className="hidden sm:inline font-cinzel text-[0.6rem] tracking-[0.2em] uppercase text-[#F0E6D2]/60 hover:text-[#FFE7A0] transition-colors duration-500"
              data-testid="packet-support-link"
            >
              Support
            </button>
            <Link to="/intake" className="rm-btn-ghost" data-testid="packet-new-btn">New Packet</Link>
          </div>
        </div>
      </header>

      {/* Cover artifact */}
      <section className="relative z-10 max-w-3xl mx-auto px-6 sm:px-12 md:px-16 pt-24 md:pt-32 pb-16">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1.2, ease: [0.25, 0.1, 0.25, 1] }}
          className="text-center mb-24"
          data-testid="packet-cover"
        >
          <div className="relative w-[180px] h-[180px] mx-auto mb-12 rm-artifact" aria-hidden="true">
            <motion.img
              src="/visuals/art_mandala.png"
              alt=""
              animate={{ rotate: 360 }}
              transition={{ duration: 240, ease: "linear", repeat: Infinity }}
              className="absolute inset-0 w-full h-full object-contain rm-relic"
              style={{ opacity: 0.95 }}
            />
          </div>
          <div className="overline mb-6">The Restoration Matrix</div>
          <h1 className="font-serif-display text-5xl sm:text-6xl lg:text-7xl leading-none text-[#F0E6D2] mb-10">
            For <span className="italic text-[#FFE7A0]" style={{ textShadow: "0 0 50px rgba(255, 231, 160,0.4)" }}>{packet.name}</span>
          </h1>

          <div className="mb-8 flex justify-center">
            <button
              type="button"
              className="rm-btn rm-breathe"
              onClick={download}
              data-testid="packet-download-btn"
            >
              Download the PDF
            </button>
          </div>

          <div
            className="mx-auto mb-10 max-w-xl border border-[#FFE7A0]/40 bg-[#FFE7A0]/5 px-5 sm:px-6 py-4 text-center"
            data-testid="packet-ephemeral-notice"
          >
            <div className="font-cinzel text-[0.65rem] sm:text-xs tracking-[0.25em] uppercase text-[#FFE7A0] mb-2">
              Keep your packet
            </div>
            <p className="text-sm sm:text-base text-[#F0E6D2]/90 font-body font-light leading-relaxed">
              This packet exists only for this session. If you navigate away
              without downloading the PDF, it is lost forever.
            </p>
          </div>

          <div className="flex items-center justify-center gap-4">
            <span className="rm-divider" />
            <span className="font-cinzel text-[0.65rem] uppercase text-[#F0E6D2]/45">Compiled {createdPretty}</span>
            <span className="rm-divider" />
          </div>
          <button
            type="button"
            onClick={() => document.querySelector('[data-testid="donate-card"]')?.scrollIntoView({ behavior: "smooth", block: "center" })}
            className="mt-8 font-cinzel text-[0.6rem] tracking-[0.25em] uppercase text-[#F0E6D2]/45 hover:text-[#FFE7A0] transition-colors duration-500 italic"
            data-testid="packet-hero-support-link"
          >
            If this packet has met you — sow into the next →
          </button>
        </motion.div>

        <div className="space-y-12" data-testid="packet-sections">
          {packet.sections.map((sec, i) => {
            const isLadderSection =
              typeof sec.title === "string"
              && sec.title.toLowerCase().includes("jacob")
              && sec.title.toLowerCase().includes("ladder")
              && sec.title.toLowerCase().includes("path");
            return (
              <React.Fragment key={`${sec.title}-${i}`}>
                <motion.article
                  initial={{ opacity: 0, y: 16 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.7, delay: Math.min(i * 0.05, 0.4), ease: [0.25, 0.1, 0.25, 1] }}
                  data-testid={`packet-section-${i}`}
                >
                  <h2 className="font-serif-display text-2xl sm:text-3xl text-[#FFE7A0] mb-5 leading-tight">
                    {sec.title}
                  </h2>
                  <div className="text-[#F0E6D2]/90 text-base sm:text-lg leading-[1.7] font-body font-light">
                {(sec.body || "")
                  .split(/\n\n+/)
                  .filter((p) => p.trim().length > 0)
                  .map((para, idx) => {
                    const lines = para
                      .split("\n")
                      .map((l) => l.trim())
                      .filter(Boolean);
                    if (lines.length === 0) return null;

                    // Single-line paragraph → maybe a rung label or inline label.
                    if (lines.length === 1) {
                      const single = lines[0];
                      const rungMatch = single.match(RUNG_LABEL_RE);
                      if (rungMatch) {
                        return (
                          <h3
                            key={idx}
                            id={rungHeadingId(rungMatch[1])}
                            className="mt-10 mb-3 font-serif-display text-xl sm:text-2xl text-[#FFE7A0] leading-snug scroll-mt-28"
                            data-testid={`packet-rung-label-${rungMatch[1].toLowerCase()}`}
                          >
                            {single}
                          </h3>
                        );
                      }
                      if (INLINE_LABEL_RE.test(single)) {
                        return (
                          <p
                            key={idx}
                            className="mt-5 mb-1 font-cinzel text-xs sm:text-sm tracking-[0.2em] uppercase text-[#FFE7A0]/85"
                            data-testid="packet-inline-label"
                          >
                            {single}
                          </p>
                        );
                      }
                      if (SUBSECTION_HEADING_RE.test(single)) {
                        // Strip optional **markdown bold** wrappers so the
                        // subsection heading renders as styled text, not
                        // raw asterisks.
                        const cleanLabel = single.replace(/^\*\*([\s\S]+?)\*\*\s*[:.]?\s*$/, "$1").replace(/^\*\*|\*\*$/g, "");
                        return (
                          <p
                            key={idx}
                            className="mt-6 mb-2 font-serif-display italic text-lg sm:text-xl text-[#FFE7A0]"
                            data-testid="packet-subsection-heading"
                          >
                            {cleanLabel}
                          </p>
                        );
                      }
                    }

                    // Tight-list detection (mirrors PDF): 3+ short lines.
                    const isList =
                      lines.length >= 3 &&
                      lines.every((l) => l.length <= 130);

                    // Category heading + list: short first line without
                    // terminal punctuation, rest look like list items.
                    const firstLooksLikeHeading =
                      lines.length >= 3 &&
                      lines[0].length <= 60 &&
                      !/[.!?]$/.test(lines[0]) &&
                      lines.slice(1).every((l) => l.length <= 130);

                    if (firstLooksLikeHeading) {
                      return (
                        <div key={idx} className="mt-6 mb-4">
                          <div className="mb-2 font-serif-display italic text-lg sm:text-xl text-[#F0E6D2]">
                            {lines[0]}
                          </div>
                          <ul className="space-y-1 list-none pl-0">
                            {lines.slice(1).map((l, li) => (
                              <li
                                key={li}
                                className="text-[#F0E6D2]/90"
                              >
                                {renderInline(l)}
                              </li>
                            ))}
                          </ul>
                        </div>
                      );
                    }

                    if (isList) {
                      return (
                        <ul key={idx} className="mt-2 mb-4 space-y-1 list-none pl-0">
                          {lines.map((l, li) => (
                            <li key={li} className="text-[#F0E6D2]/90">
                              {renderInline(l)}
                            </li>
                          ))}
                        </ul>
                      );
                    }

                    // Normal flowing paragraph.
                    return (
                      <p key={idx} className="mb-4">
                        {renderInline(lines.join(" "))}
                      </p>
                    );
                  })}
              </div>
            </motion.article>
            {isLadderSection && (
              <LadderDiagram activeRungs={detectActiveBlockageRungs(packet.sections)} />
            )}
          </React.Fragment>
            );
          })}
        </div>

        {packet.specifier && (
          <div
            className="mt-20 mx-auto max-w-3xl text-center"
            data-testid="packet-diagnostic-specifier-block"
          >
            <div className="flex items-center justify-center gap-4 mb-6">
              <span className="rm-divider" />
              <span className="font-cinzel text-[0.6rem] tracking-[0.3em] uppercase text-[#F0E6D2]/45">
                Diagnostic Signature
              </span>
              <span className="rm-divider" />
            </div>
            <div
              className="text-sm sm:text-base italic text-[#F0E6D2]/75 font-body font-light tracking-wide"
              data-testid="packet-diagnostic-specifier"
            >
              Diagnostic specifier &mdash;{" "}
              severity: {packet.specifier.severity || "moderate"} &middot;{" "}
              course: {packet.specifier.course || "episodic"} &middot;{" "}
              insight: {packet.specifier.insight || "partial"} &middot;{" "}
              agency: {packet.specifier.agency || "mixed"} &middot;{" "}
              mix: {packet.specifier.mixture || "mixed"}.
            </div>
            <p
              className="mt-3 mx-auto max-w-2xl text-xs sm:text-sm italic leading-relaxed text-[#F0E6D2]/50 font-body font-light"
              data-testid="packet-specifier-legend"
            >
              Severity gauges how heavy the wound is. Course names its
              shape in time &mdash; acute (a single event), episodic
              (recurring), chronic (long-running), or generational
              (family-line). Insight reflects how clearly you can
              currently see the pattern at work in you. Agency reflects
              how much forward movement you can choose right now. Mix
              names whether the load is mainly wound, mainly sin,
              mainly affliction, or a blend.
            </p>
          </div>
        )}

        <div className="mt-28 text-center" data-testid="packet-footer-cta">
          <button className="rm-btn rm-breathe" onClick={download} data-testid="packet-download-btn-bottom">
            Download the PDF
          </button>
        </div>

        {/* Pastoral donation invitation */}
        <Donate packetId={id} />
      </section>
    </main>
  );
}
